import { applySnapshot, flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import i18n from "../../i18n";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import { ActionModel } from "../org/OrganizeModel";
import {
  IProjTypeLoanByYearModel,
  ProjTypeLoanByYearModel,
} from "../project/ProjectModel";
import { CurrencyModel, SelectListModel } from "../share";
import { PlanStatusLogModel } from "../share/PlanModel";
import { FiveYearPlanBodyModel } from "./FiveYearPlanBodyModel";
import fiveYearPlanAPI, {
  IFiveYearPlanStaffSummarQuery,
} from "./FiveYearPlanService";

export const FiveYearPlanHeaderModel = types
  .model("FiveYearPlanHeaderModel", {
    planID: customtypes.optional(types.number, 0),
    planCode: customtypes.optional(types.string, ""),
    planYear: customtypes.optional(types.number, 0),
    planYearTxt: customtypes.optional(types.string, ""),
    planRelease: customtypes.optional(types.string, ""),
    createLog: types.maybeNull(ActionModel),
    changeLog: types.maybeNull(ActionModel),
    planStatus: customtypes.optional(types.string, ""),
    organization: types.optional(SelectListModel, {}),
    proposals: types.optional(types.array(SelectListModel), []),
    planStatusLog: customtypes.optional(PlanStatusLogModel, {}),
    error: types.optional(ErrorModel, {}),
  })
  .views((self: any) => ({
    get changeLogActionTime() {
      return self.changeLog ? self.changeLog.actionTime || "-" : "-";
    },
    get changeLogUsername() {
      return self.changeLog ? self.changeLog.userName || "-" : "-";
    },
    get createLogActionTime() {
      return self.createLog ? self.createLog.actionTime || "-" : "-";
    },
    get createLogUsername() {
      return self.createLog ? self.createLog.userName || "-" : "-";
    },
    get description() {
      return `แผน 5 ปี [${self.planYearTxt} ${self.planRelease}]`;
    },
  }))
  .actions((self: any) => ({
    createFiveYearPlan: flow(function*() {
      try {
        const result = yield fiveYearPlanAPI.create({
          planRelease: self.planRelease,
          startYear: self.planYear,
          organizationID: self.organization.id,
        });
        return result;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type IFiveYearPlanHeaderModel = typeof FiveYearPlanHeaderModel.Type;

export const FiveYearPlanSummaryModel = types
  .model("FiveYearPlanSummaryModel", {
    description: customtypes.optional(types.string, ""),
    planHeader: customtypes.optional(FiveYearPlanHeaderModel, {}),
    loanByProjType: types.optional(types.array(ProjTypeLoanByYearModel), []),
    currencyData: types.maybeNull(CurrencyModel),
    selectedProjectType: types.optional(types.string, "ALL"),
  })
  .views((self: any) => ({
    get loanByProjTypeFilterType() {
      const filtering: any = [];
      self.loanByProjType.forEach((projectType: IProjTypeLoanByYearModel) => {
        filtering.push(projectType.filterOption);
      });
      return filtering;
    },
    get selectedLoanByProjType(): IProjTypeLoanByYearModel {
      return self.loanByProjType.find(
        (projectType: IProjTypeLoanByYearModel) =>
          projectType.projectTypeCode === self.selectedProjectType,
      );
    },
  }))
  .actions((self: any) => ({
    onSelectedProjectType: (projectTypeCode: string) => {
      self.selectedProjectType = projectTypeCode;
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
  }));
export type IFiveYearPlanSummaryModel = typeof FiveYearPlanSummaryModel.Type;

export const FiveYearPlanModel = types
  .model("FiveYearPlanModel", {
    summary: types.optional(FiveYearPlanSummaryModel, {}),
    body: types.optional(FiveYearPlanBodyModel, {}),
    error: types.optional(ErrorModel, {}),
    planId: types.maybe(types.number),
    loading: types.optional(types.boolean, false),
  })
  .views((self: any) => ({
    //
  }))
  .actions((self: any) => ({
    getFiveYearPlanSummary: flow(function*() {
      try {
        self.loading = true;
        const result = yield fiveYearPlanAPI.getFiveYearPlanSummary(
          self.planId,
          {
            startYear: self.summary.planHeader.planYear,
          },
        );
        self.summary = result.data;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    getFiveYearPlanStaffSummary: flow(function*(
      startYear: number,
      orgId?: number | null,
      planRelease?: string,
      planID?: number,
    ) {
      try {
        self.loading = true;
        if (!startYear) {
          throw {
            message: i18n.t("FiveYearPlanModel.pleaseSelectYear"),
          };
        }
        const query: IFiveYearPlanStaffSummarQuery = {
          startYear,
        };
        if (planRelease) {
          query.PlanRelease = planRelease;
        }
        if (orgId) {
          query.orgID = orgId;
        }
        if (planID) {
          query.planID = planID;
        }
        const result = yield fiveYearPlanAPI.getFiveYearPlanStaffSummary(query);
        console.log("result", result.data);
        self.summary = result.data;
      } catch (e) {
        self.summary = {};
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    setPlanId: (id: number) => {
      self.planId = id;
    },
    setPlanYear: (playYear: number) => {
      self.summary.planHeader.planYear = playYear;
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    resetAll: () => {
      applySnapshot(self, {});
    },
  }));
export type IFiveYearPlanModel = typeof FiveYearPlanModel.Type;
